//#region libraries
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

// external libraries
import { FiCopy, FiRefreshCw } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// custom components
import { NewScreen, Card } from '_atoms';
import { TableHeader } from '_molecules';

// bootstrap
import { Badge, Button, Form, Modal, Row, Col, Table, Spinner } from 'react-bootstrap';

// services
import { getPaxDeskRequests, confirmPaxRequest, rejectPaxRequest } from '_services/pax-desk-service';

// hooks
import useIdleLogout from '../../../hooks/use-idle-logout';

// styles
import { Colors } from '_styles';

import RequestRow from './request-row';
import DeskPagination from './pagination';
import { defaultRange, validateRange, confirmQuestion, confirmationLabel, END_LABELS, REJECT_REASONS, presetRange, activePreset, formatShortDateTime, formatDateTime, airportName } from './format';
//#endregion

const DEFAULT_PAGE_SIZE = 50;
const IDLE_LOGOUT_MS = 6 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_LENGTH = 60;

// Passenger requests desk — the only page the PAX_REQUESTS_DESK role can reach.
// In airlineMode (the AIRLINE_PAX_DESK role) it talks to the carrier-scoped routes and only shows
// and confirms the airline leg.
const PaxRequests = ({ airlineMode = false }) => {
  const history = useHistory();

  // shared-PC hardening: log out after 15 minutes without activity
  useIdleLogout(IDLE_LOGOUT_MS);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  //#region state
  const [now, setNow] = useState(() => Date.now());
  const [view, setView] = useState('open');
  const [sort, setSort] = useState('upcoming');
  const [range, setRange] = useState(defaultRange);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({}); // { [id]: { origin: true } }
  const [confirming, setConfirming] = useState(null); // { request, end }
  const [rejecting, setRejecting] = useState(null); // request
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusyId, setRejectBusyId] = useState(null);
  const [viewing, setViewing] = useState(null); // request shown in the passenger info modal
  //#endregion

  const rangeError = validateRange(range.from, range.to);
  const latestFetch = useRef(0);
  const loggingOut = useRef(false);
  const tableTop = useRef(null);

  // 401/403: the session is no longer valid for the desk — sign out
  const handleAuthError = useCallback((e) => {
    if (e?.status === 401 || e?.status === 403) {
      if (!loggingOut.current) {
        loggingOut.current = true;
        history.push('/logout');
      }
      return true;
    }
    return false;
  }, [history]);

  //#region data
  const fetchRequests = useCallback(async ({ quiet = false } = {}) => {
    if (rangeError) return;
    const fetchId = ++latestFetch.current;
    if (!quiet) setLoading(true);

    try {
      const result = await getPaxDeskRequests({
        view,
        sort,
        from: range.from,
        to: range.to,
        q: search || undefined,
        limit: pageSize,
        skip,
      }, { airline: airlineMode });
      if (fetchId !== latestFetch.current) return; // a newer fetch has started
      // the list shrank under us (confirms, refresh) — step back to the last page that has rows
      if (skip > 0 && skip >= result.total) {
        setSkip(Math.max(0, Math.floor((result.total - 1) / pageSize) * pageSize));
        return;
      }
      setRequests(result.requests);
      setTotal(result.total);
      setError(null);
    } catch (e) {
      if (fetchId !== latestFetch.current) return;
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
        if (!quiet) setError('Too many requests, wait a moment.');
      } else if (!quiet) {
        setError(e?.message || 'Could not load passenger requests.');
      }
    } finally {
      if (fetchId === latestFetch.current && !quiet) setLoading(false);
    }
  }, [view, sort, range.from, range.to, search, skip, pageSize, rangeError, handleAuthError, airlineMode]);

  // load on filter change
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setSkip(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  //#endregion

  //#region confirm
  const setEndPending = (id, end, value) => {
    setPending((prev) => {
      const forId = { ...(prev[id] || {}) };
      if (value) forId[end] = true;
      else delete forId[end];
      return { ...prev, [id]: forId };
    });
  };

  const submitConfirm = async () => {
    if (!confirming) return;
    const { request, end } = confirming;
    setConfirming(null);
    if (pending[request.id]?.[end]) return;
    setEndPending(request.id, end, true);

    try {
      const result = await confirmPaxRequest(request.id, end, { airline: airlineMode });
      const updated = result?.request;
      if (updated && updated.id) {
        if (belongsInView(updated)) {
          // still open (e.g. another end is pending) — keep it and refresh the row/modal
          setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
          setViewing((current) => (current?.id === updated.id ? updated : current));
        } else {
          // fully handled for this view — drop it from the open list straight away
          setRequests((prev) => prev.filter((r) => r.id !== updated.id));
          setTotal((n) => Math.max(0, n - 1));
          setViewing((current) => (current?.id === updated.id ? null : current));
        }
      }
      if (result?.alreadyConfirmed) {
        const who = confirmationLabel(updated?.confirmations?.[end], updated || request, end);
        toast.info(who ? `Already confirmed by ${who}` : 'Already confirmed');
      } else {
        toast.success(`${END_LABELS[end]} confirmed for ${request.name || 'passenger'}`);
      }
    } catch (e) {
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
      } else {
        toast.error(e?.message || 'Could not confirm, please try again.');
      }
      // 404/409: the request changed underneath us — reload it
      if (e?.status === 404 || e?.status === 409) fetchRequests({ quiet: true });
    } finally {
      setEndPending(request.id, end, false);
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    const request = rejecting;
    const reason = rejectReason;
    setRejecting(null);
    setRejectReason('');
    setRejectBusyId(request.id);

    try {
      const result = await rejectPaxRequest(request.id, reason, { airline: airlineMode });
      latestFetch.current += 1;
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      setViewing((current) => (current?.id === request.id ? null : current));
      setTotal((n) => Math.max(0, n - 1));
      if (result?.alreadyRejected) {
        toast.info(`${request.name || 'Passenger'} was already rejected`);
      } else {
        toast.success(`${request.name || 'Passenger'} rejected`);
      }
    } catch (e) {
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
      } else {
        toast.error(e?.message || 'Could not reject, please try again.');
      }
      if (e?.status === 404 || e?.status === 409) fetchRequests({ quiet: true });
    } finally {
      setRejectBusyId(null);
    }
  };

  //#endregion

  //#region handlers
  const changeView = (value) => {
    setView(value);
    setSkip(0);
  };

  const changeSort = (value) => {
    setSort(value);
    setSkip(0);
  };

  const applyPreset = (preset) => {
    setRange(presetRange(preset));
    setSkip(0);
  };

  const changeRange = (key, value) => {
    setRange((prev) => ({ ...prev, [key]: value }));
    setSkip(0);
  };

  const changeSkip = (value) => {
    setSkip(value);
    tableTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changePageSize = (value) => {
    // keep the first visible row on screen
    setSkip(Math.floor(skip / value) * value);
    setPageSize(value);
  };

  //#endregion

  const pager = (
    <DeskPagination
      total={total}
      skip={skip}
      pageSize={pageSize}
      shown={requests.length}
      disabled={loading}
      onSkipChange={changeSkip}
      onPageSizeChange={changePageSize}
    />
  );

  // Does this request still belong in the list the desk is currently looking at?
  // Open = not rejected and still needing a confirm (airline leg for the airline desk,
  // any outstanding end for the normal desk). Used to drop a row the moment it's confirmed.
  const belongsInView = (r) => {
    if (view === 'rejected') return !!r.rejected;
    const confirmed = airlineMode ? !!r.confirmations?.airline?.confirmed : r.status === 'confirmed';
    if (view === 'confirmed') return confirmed;
    return !r.rejected && !confirmed;
  };

  // Airline desk: only the airline leg, and only while it still needs confirming.
  // Normal desk: always show all three ends so staff can see each one at a glance.
  const confirmEndsFor = (request) =>
    airlineMode
      ? ['airline'].filter((end) => request.required?.[end] && !request.confirmations?.[end]?.confirmed)
      : ['origin', 'destination', 'airline'];

  const confirmButtons = (request) => confirmEndsFor(request).map((end) => {
    const busy = !!pending[request.id]?.[end];
    if (airlineMode) {
      return (
        <Button
          key={end}
          variant="primary"
          disabled={busy}
          onClick={() => setConfirming({ request, end })}
        >
          {busy ? 'Confirming…' : 'Confirm'}
        </Button>
      );
    }
    const required = !!request.required?.[end];
    const confirmed = !!request.confirmations?.[end]?.confirmed;
    return (
      <div key={end} style={styles.confirmGroup}>
        <div style={styles.confirmEndLabel}>{endCaption(request, end)}</div>
        {!required ? (
          <Button variant="outline-secondary" disabled>Not required</Button>
        ) : (
          <Button
            disabled={confirmed || busy}
            style={confirmed ? styles.confirmedButton : styles.confirmButton}
            onClick={() => { if (!confirmed) setConfirming({ request, end }); }}
          >
            {busy ? 'Confirming…' : confirmed ? 'Confirmed' : 'Confirm'}
          </Button>
        )}
      </div>
    );
  });

  const preset = activePreset(range);
  const emptyCopy = view === 'open'
    ? 'No open passenger requests for these dates.'
    : view === 'confirmed'
      ? 'No confirmed passenger requests for these dates.'
      : 'No rejected passenger requests for these dates.';

  return (
    <NewScreen fluid>
      <div className="mt-3" style={{ paddingBottom: 22 }}>
        <Card>
          {/* open / confirmed / rejected on the left, refresh on the far right */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button variant={view === 'open' ? 'primary' : 'outline-secondary'} onClick={() => changeView('open')}>Open</Button>
              <Button variant={view === 'confirmed' ? 'primary' : 'outline-secondary'} onClick={() => changeView('confirmed')}>Confirmed</Button>
              <Button variant={view === 'rejected' ? 'primary' : 'outline-secondary'} onClick={() => changeView('rejected')}>Rejected</Button>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Button
                variant="outline-primary"
                aria-label="Refresh"
                title="Refresh"
                onClick={() => fetchRequests()}
                disabled={loading || !!rangeError}
                style={styles.iconButton}
              >
                {loading ? (
                  <Spinner animation="border" role="status" style={styles.refreshSpinner} />
                ) : (
                  <FiRefreshCw size={16} />
                )}
              </Button>
            </div>
          </div>

          {/* filters */}
          <Row className="mt-3 g-2 align-items-end">
            <Col xs={12} md={3}>
              <Form.Label style={styles.label}>Search</Form.Label>
              <Form.Control
                type="search"
                placeholder="Name, flight, PNR or booking ID"
                value={searchInput}
                maxLength={MAX_SEARCH_LENGTH}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>From</Form.Label>
              <Form.Control
                type="date"
                value={range.from}
                onChange={(e) => changeRange('from', e.target.value)}
                isInvalid={!!rangeError}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>To</Form.Label>
              <Form.Control
                type="date"
                value={range.to}
                onChange={(e) => changeRange('to', e.target.value)}
                isInvalid={!!rangeError}
              />
            </Col>
            <Col xs={12} md="auto">
              <Form.Label style={styles.label}>Show</Form.Label>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant={preset === 'today' ? 'primary' : 'outline-secondary'} onClick={() => applyPreset('today')}>Today</Button>
                <Button variant={preset === 'week' ? 'primary' : 'outline-secondary'} onClick={() => applyPreset('week')}>This week</Button>
                <Button variant={preset === 'month' ? 'primary' : 'outline-secondary'} onClick={() => applyPreset('month')}>This month</Button>
              </div>
            </Col>
            <Col xs={12} md="auto" className="ms-md-auto">
              <Form.Label style={styles.label}>Sort</Form.Label>
              <Form.Select
                aria-label="Sort requests"
                value={sort}
                onChange={(e) => changeSort(e.target.value)}
                style={{ width: 180 }}
              >
                <option value="upcoming">Upcoming first</option>
                <option value="newest">Newest first</option>
              </Form.Select>
            </Col>
          </Row>
          {rangeError ? (
            <div style={styles.rangeError}>{rangeError}</div>
          ) : null}

          {/* table */}
          <div ref={tableTop} className="mt-3" />
          {requests.length > 0 && !error ? pager : null}
          <div style={{ overflowX: 'auto', minHeight: 200 }}>
            {error ? (
              <div className="d-flex flex-column align-items-center mt-5">
                <p style={{ color: '#C0392B' }}>{error}</p>
                <Button variant="outline-primary" size="sm" onClick={() => fetchRequests()}>Try again</Button>
              </div>
            ) : requests.length === 0 ? (
              loading ? null : (
                <div className="d-flex flex-column align-items-center mt-5" style={{ opacity: 0.6 }}>
                  <p>{emptyCopy}</p>
                </div>
              )
            ) : (
              <Table hover responsive style={{ opacity: loading ? 0.6 : 1 }}>
                <thead>
                  <tr>
                    <th><TableHeader title="Name" /></th>
                    <th />
                    <th><TableHeader title="Flight" /></th>
                    <th><TableHeader title="From" /></th>
                    <th><TableHeader title="To" /></th>
                    <th><TableHeader title="PNR" /></th>
                    <th><TableHeader title="STD" /></th>
                    <th><TableHeader title="Requested" /></th>
                    <th><TableHeader title="Created" /></th>
                    <th><TableHeader title="Departs" /></th>
                    <th><TableHeader title="Notes" /></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <RequestRow
                      key={request.id}
                      request={request}
                      now={now}
                      onView={(r) => setViewing(r)}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* pagination */}
          {requests.length > 0 && !error ? pager : null}
        </Card>
      </div>

      {/* confirm dialog */}
      <Modal show={!!confirming} onHide={() => setConfirming(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{confirming ? `Confirm ${END_LABELS[confirming.end].toLowerCase()}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirming ? confirmQuestion(confirming.request, confirming.end) : ''}
          <div style={{ ...styles.small, marginTop: 10 }}>This can’t be undone. The passenger will see it in the app.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirming(null)}>Cancel</Button>
          <Button variant="primary" onClick={submitConfirm}>Confirm</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!rejecting} onHide={() => { setRejecting(null); setRejectReason(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>Reject request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejecting ? `Reject assistance for ${rejecting.name || 'this passenger'}?` : ''}
          <Form.Select
            aria-label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{ marginTop: 12 }}
          >
            <option value="">Choose a reason</option>
            {REJECT_REASONS.map((reason) => (
              <option key={reason.id} value={reason.id}>{reason.label}</option>
            ))}
          </Form.Select>
          <div style={{ ...styles.small, marginTop: 10 }}>
            This can’t be undone. The passenger will see the request as rejected in the app, and it will be ignored for airport assistance.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setRejecting(null); setRejectReason(''); }}>Cancel</Button>
          <Button variant="danger" onClick={submitReject}>Reject</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!viewing} onHide={() => setViewing(null)}>
        <Modal.Header closeButton />
        <Modal.Body>
          {viewing ? (
            <>
              <div style={styles.fieldRow}>
                <InfoLine label="Name" copy value={viewing.name} />
                <InfoLine label="Assistance">
                  {(viewing.assistance || []).length ? [...new Set(viewing.assistance)].map((code) => (
                    <Badge key={code} bg="primary" style={styles.code}>{code}</Badge>
                  )) : null}
                </InfoLine>
              </div>
              <div style={styles.fieldRow}>
                <InfoLine label="PNR" copy value={viewing.pnr} />
                <InfoLine label="Requested" value={formatShortDateTime(viewing.createdAt)} />
              </div>
              <div style={styles.fieldRow}>
                <InfoLine label="Flight" value={[viewing.flight?.carrierCode, viewing.flight?.flightNumber].filter(Boolean).join(' ')} />
                <InfoLine label="STD" value={formatShortDateTime(viewing.flight?.departureDate, viewing.flight?.departureTime)} />
              </div>
              <div style={styles.fieldRow}>
                <InfoLine label="From" value={viewing.origin?.iata} />
                <InfoLine label="To" value={viewing.destination?.iata} />
              </div>
              <div style={styles.fieldStack}>
                <InfoLine label="Email" copy value={viewing.email} />
                <InfoLine label="Booking ID" copy value={viewing.id} />
                <InfoLine label="Notes" value={viewing.bio} />
              </div>
              <div style={styles.statusBlock}>
                {(airlineMode ? ['airline'] : ['origin', 'destination', 'airline']).map((end) => (
                  <ConfirmStatus key={end} request={viewing} end={end} />
                ))}
              </div>
            </>
          ) : null}
        </Modal.Body>
        {viewing && view !== 'rejected' ? (
          <Modal.Footer style={styles.actionFooter}>
            <Button
              variant="outline-danger"
              disabled={rejectBusyId === viewing.id}
              onClick={() => { setRejectReason(''); setRejecting(viewing); }}
            >
              {rejectBusyId === viewing.id ? 'Rejecting…' : 'Reject'}
            </Button>
            <div style={styles.confirmActions}>{confirmButtons(viewing)}</div>
          </Modal.Footer>
        ) : null}
      </Modal>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={true} />
    </NewScreen>
  );
};

// Full airport name (or airline name) for an end, falling back to the IATA code.
const endCaption = (request, end) => {
  if (end === 'airline') return request.flight?.airlineName || 'Airline';
  const airport = end === 'origin' ? request.origin : request.destination;
  return airportName(airport?.name) || airport?.iata || (end === 'origin' ? 'Departure' : 'Arrival');
};

// One simple status line under Notes: end name + whether it's confirmed, by who and when.
const ConfirmStatus = ({ request, end }) => {
  const required = !!request?.required?.[end];
  const confirmation = request?.confirmations?.[end];
  let status;
  let color;
  if (confirmation?.confirmed) {
    const who = confirmation.by?.displayName || confirmationLabel(confirmation, request, end);
    const when = formatDateTime(confirmation.at, true);
    status = `Confirmed${who ? ` by ${who}` : ''}${when ? ` · ${when}` : ''}`;
    color = '#1f9d55';
  } else if (!required) {
    status = end === 'airline' ? 'Arranged with the airline' : 'Not required';
    color = Colors.PRM_GRAY;
  } else {
    status = 'Awaiting confirmation';
    color = '#B9770E';
  }
  return (
    <div style={styles.statusLine}>
      <div style={styles.statusEnd}>{endCaption(request, end)}</div>
      <div style={{ ...styles.statusValue, color }}>{status}</div>
    </div>
  );
};

const copyValue = async (label, value) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch (e) {
    toast.error('Could not copy');
  }
};

const InfoLine = ({ label, value, copy, children }) => (
  <div style={styles.fieldItem}>
    <div style={styles.label}>{label}</div>
    <div style={styles.valueRow}>
      <div style={styles.fieldValue}>
        {children || value || '—'}
      </div>
      {copy && value ? (
        <button type="button" aria-label={`Copy ${label}`} title={`Copy ${label}`} style={styles.copyButton} onClick={() => copyValue(label, value)}>
          <FiCopy size={14} />
        </button>
      ) : null}
    </div>
  </div>
);

const styles = {
  fieldRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 14,
  },
  fieldItem: {
    flex: '1 1 0',
    minWidth: 0,
  },
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  statusBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 18,
    paddingTop: 14,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  statusLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  statusEnd: {
    fontSize: 13,
    color: Colors.PRM_GRAY,
  },
  statusValue: {
    fontSize: 13,
    textAlign: 'right',
  },
  actionFooter: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  confirmActions: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
  },
  confirmGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    maxWidth: 160,
  },
  confirmEndLabel: {
    fontSize: 12,
    color: Colors.PRM_GRAY,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  confirmButton: {
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    borderColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
  },
  confirmedButton: {
    backgroundColor: '#1f9d55',
    borderColor: '#1f9d55',
    color: '#fff',
    opacity: 1,
    cursor: 'not-allowed',
  },
  label: {
    fontSize: 13,
    opacity: 0.7,
    marginBottom: 2,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'none',
    color: Colors.PRM_LIGHT_BLUE,
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  fieldValue: {
    fontSize: 14,
    color: Colors.PRM_GRAY,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  code: {
    fontSize: 11,
    marginRight: 4,
  },
  small: {
    fontSize: 12,
    opacity: 0.6,
  },
  rangeError: {
    fontSize: 12,
    color: '#C0392B',
    marginTop: 4,
  },
  iconButton: {
    width: 38,
    height: 38,
    minWidth: 38,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 38px',
  },
  refreshSpinner: {
    width: 16,
    height: 16,
    borderWidth: 2,
  },
};

export default PaxRequests;
